import React, { forwardRef, RefObject, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    LanguageSelectorOptionsWrapper,
    LanguageSelectorMenuButton,
    LanguageSelectorButtonText,
    LanguageSelectorOption,
    LanguageSelectorArrowWrapper,
} from './Footer.style';
import { PopperBase } from '@toluna-ui-toolkit/popper-base';
import { Culture } from '../data/models';
import ChevronIcon from '../../../assets/icons/chevron.svg?react';
import { FocusTrapActivator } from '../../common/components/FocusTrapActivator';

interface ILanguageSelectors {
    supportedLanguages: Partial<Culture>[];
    onSelectLanguage: (language: Partial<Culture>) => void;
    selectedCulture: Partial<Culture>;
    handleFocusParent: () => void;
}

export interface LanguageSelectorRef {
    toggleLanguageMenu: () => void;
}

type PopperInstance = {
    toggle: () => void;
    recalculatePosition: () => void;
    isOpen: boolean;
};
export const LanguageSelector = forwardRef<LanguageSelectorRef, ILanguageSelectors>(
    ({ supportedLanguages, onSelectLanguage, selectedCulture, handleFocusParent }: ILanguageSelectors, ref) => {
        const languageRef = useRef<HTMLDivElement | null>(null);
        const languagePopperRef: RefObject<PopperInstance> = useRef<PopperInstance | null>(null);
        const [focusReady, setFocusReady] = useState(false);
        const [isMenuOpen, setIsMenuOpen] = useState(false);

        useImperativeHandle(ref, () => ({
            toggleLanguageMenu: () => {
                if (languagePopperRef.current) {
                    const isCurrentlyOpen = isMenuOpen;
                    languagePopperRef.current?.toggle();
                    setIsMenuOpen(!isCurrentlyOpen);
                }
            },
        }));

        useEffect(() => {
            if (isMenuOpen) {
                const timeout = setTimeout(() => {
                    if (languageRef.current) setFocusReady(true);
                }, 0);
                return () => clearTimeout(timeout);
            } else setFocusReady(false);
        }, [isMenuOpen]);

        const handleLanguageSelection = (language: Partial<Culture>) => {
            languagePopperRef.current?.toggle();
            onSelectLanguage && onSelectLanguage(language);
        };

        const handleKeyDownLanguageSelection = (event: React.KeyboardEvent, language: Partial<Culture>) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleLanguageSelection(language);
            }
        };

        const getLanguageOptions = () => {
            return (
                supportedLanguages &&
                selectedCulture.id &&
                supportedLanguages.map((language, ind) => {
                    if (language.id !== selectedCulture.id) {
                        return (
                            <LanguageSelectorOption
                                tabIndex={0}
                                key={language.id + '' + ind} // TODO : remove ind, it's here because of BE data issue
                                onClick={() => handleLanguageSelection(language)}
                                onKeyDown={event => handleKeyDownLanguageSelection(event, language)}
                            >
                                {language.name}
                            </LanguageSelectorOption>
                        );
                    } else {
                        return null;
                    }
                })
            );
        };

        const onEscape = () => {
            languagePopperRef.current?.toggle();
            setIsMenuOpen(false);
            handleFocusParent();
        };

        return (
            <>
                <PopperBase
                    trigger="click"
                    closeOnClickOutside
                    ref={languagePopperRef as any}
                    popperElement={
                        <>
                            <LanguageSelectorOptionsWrapper ref={languageRef}>
                                {getLanguageOptions()}
                            </LanguageSelectorOptionsWrapper>
                            {focusReady && <FocusTrapActivator containerRef={languageRef} onEscape={onEscape} />}
                        </>
                    }
                >
                    <LanguageSelectorMenuButton>
                        <LanguageSelectorButtonText>{selectedCulture.name}</LanguageSelectorButtonText>
                        <LanguageSelectorArrowWrapper>
                            <ChevronIcon />
                        </LanguageSelectorArrowWrapper>
                    </LanguageSelectorMenuButton>
                </PopperBase>
            </>
        );
    }
);
